/**
 * OrderAmendPanel — admin proposes line removals / quantity reductions on an
 * order and sends the request for customer approval (BMF-BRD-2026-014).
 * Rendered inside the existing order-details modal in OrderManagement.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Send, Loader2 } from 'lucide-react';
import orderService from '../../services/orderService';

const REASON_CODES = [
  { value: 'QUALITY_REJECTED', label: 'Quality rejected' },
  { value: 'OUT_OF_STOCK', label: 'Out of stock' },
  { value: 'PARTIAL_STOCK', label: 'Only partial stock available' },
  { value: 'DAMAGED', label: 'Damaged in handling' },
  { value: 'OTHER', label: 'Other' },
];

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const OrderAmendPanel = ({ order, onDone }) => {
  // Per-line editable state keyed by orderItemId.
  const [rows, setRows] = useState(() =>
    order.items.map((it) => ({
      orderItemId: it.orderItemId,
      name: it.name,
      unitPrice: it.price,
      originalQty: it.quantity,
      action: 'none', // none | remove | reduce
      proposedQuantity: it.quantity,
      reasonCode: '',
      note: '',
    })),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const setRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.orderItemId === id ? { ...r, ...patch } : r)));

  // Changed lines = those with an actual action selected.
  const changed = rows.filter((r) => r.action === 'remove' || r.action === 'reduce');

  const originalTotal = rows.reduce((s, r) => s + r.unitPrice * r.originalQty, 0);
  const revisedProductTotal = rows.reduce((s, r) => {
    const qty = r.action === 'remove' ? 0 : r.action === 'reduce' ? Number(r.proposedQuantity) : r.originalQty;
    return s + r.unitPrice * qty;
  }, 0);
  const reduction = originalTotal - revisedProductTotal;

  // Validation mirrors the backend rules (BRD 2.2).
  const lineValid = (r) => {
    if (r.action === 'none') return true;
    if (!r.reasonCode) return false;
    if ((r.note || '').trim().length < 10 || (r.note || '').length > 250) return false;
    if (r.action === 'reduce') {
      const q = Number(r.proposedQuantity);
      if (!Number.isInteger(q) || q < 1 || q >= r.originalQty) return false;
    }
    return true;
  };
  const remainingUnits = rows.reduce((s, r) => {
    const qty = r.action === 'remove' ? 0 : r.action === 'reduce' ? Number(r.proposedQuantity) : r.originalQty;
    return s + qty;
  }, 0);
  const allLinesValid = changed.length > 0 && changed.every(lineValid);
  const emptyOrder = remainingUnits <= 0;
  const canSubmit = allLinesValid && !emptyOrder && !submitting;

  const submit = async () => {
    setError('');
    if (emptyOrder) {
      setError('This amendment removes every item — cancel the order instead.');
      return;
    }
    setSubmitting(true);
    const items = changed.map((r) => ({
      orderItemId: r.orderItemId,
      action: r.action,
      proposedQuantity: r.action === 'remove' ? 0 : Number(r.proposedQuantity),
      reasonCode: r.reasonCode,
      note: r.note.trim(),
    }));
    try {
      const res = await orderService.createAmendment(order.id, items);
      if (res.success) {
        onDone?.(true);
      } else {
        setError(res.error || 'Failed to send for approval');
      }
    } catch (err) {
      setError(err?.message || 'Failed to send for approval');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t border-gray-200 pt-4">
      <h4 className="font-medium text-gray-800 mb-1">Amend Order</h4>
      <p className="text-xs text-gray-500 mb-3">
        Select affected items, reduce deliverable quantity or remove, add reason/note, and send for customer approval.
      </p>

      <div className="space-y-3">
        {rows.map((r) => (
          <div
            key={r.orderItemId}
            className={`p-3 rounded-lg border ${
              r.action !== 'none' ? 'border-[#548B2E] bg-[#F1F8E9]' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[140px]">
                <p className="font-medium text-gray-800 text-sm">{r.name}</p>
                <p className="text-xs text-gray-500">
                  Ordered {r.originalQty} × {formatINR(r.unitPrice)}
                </p>
              </div>
              <select
                value={r.action}
                onChange={(e) =>
                  setRow(r.orderItemId, {
                    action: e.target.value,
                    proposedQuantity: e.target.value === 'reduce' ? Math.max(1, r.originalQty - 1) : r.originalQty,
                  })
                }
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
              >
                <option value="none">None</option>
                <option value="reduce" disabled={r.originalQty <= 1}>Reduce Qty</option>
                <option value="remove">Remove Item</option>
              </select>
              {r.action === 'reduce' && (
                <input
                  type="number"
                  min={1}
                  max={r.originalQty - 1}
                  value={r.proposedQuantity}
                  onChange={(e) => setRow(r.orderItemId, { proposedQuantity: e.target.value })}
                  className="w-20 text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                  placeholder="Qty"
                />
              )}
              {r.action === 'remove' && (
                <span className="text-xs font-medium text-red-600">Marked for removal</span>
              )}
            </div>

            {r.action !== 'none' && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <select
                  value={r.reasonCode}
                  onChange={(e) => setRow(r.orderItemId, { reasonCode: e.target.value })}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                >
                  <option value="">Select reason…</option>
                  {REASON_CODES.map((rc) => (
                    <option key={rc.value} value={rc.value}>{rc.label}</option>
                  ))}
                </select>
                <textarea
                  value={r.note}
                  onChange={(e) => setRow(r.orderItemId, { note: e.target.value })}
                  rows={1}
                  placeholder="Admin note (10–250 chars)"
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 resize-y"
                />
                {r.action === 'reduce' &&
                  !(Number.isInteger(Number(r.proposedQuantity)) && Number(r.proposedQuantity) >= 1 && Number(r.proposedQuantity) < r.originalQty) && (
                    <p className="sm:col-span-2 text-xs text-red-600">
                      Revised quantity must be less than ordered quantity and at least 1.
                    </p>
                  )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Approval preview */}
      <div className="mt-4 rounded-xl border border-gray-200 p-3 bg-white">
        <p className="text-sm font-semibold text-gray-700 mb-2">Approval Preview</p>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Original Total</span><span>{formatINR(originalTotal)}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Reduction</span><span>- {formatINR(reduction)}</span>
        </div>
        <div className="flex justify-between text-sm font-semibold text-gray-800 mt-1 border-t border-gray-100 pt-1">
          <span>Revised Total</span><span>{formatINR(revisedProductTotal + (order.shipping || 0))}</span>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">Delivery {formatINR(order.shipping || 0)} unchanged. Server re-validates on submit.</p>
      </div>

      {emptyOrder && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          This amendment removes every item. Cancel the order instead.
        </div>
      )}
      {error && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          Proposed quantity must be less than ordered quantity and at least 1. Reason and note are mandatory.
        </p>
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium bg-[#548B2E] hover:bg-[#456f26] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send for Approval
        </button>
      </div>
    </div>
  );
};

OrderAmendPanel.propTypes = {
  order: PropTypes.object.isRequired,
  onDone: PropTypes.func,
};

export default OrderAmendPanel;
